import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import type { IconType } from 'react-icons';
import { FiEdit2 } from 'react-icons/fi';
import {
  TbFaceId,
  TbFingerprint,
  TbKey,
  TbLifebuoy,
  TbMail,
  TbPhone,
  TbScanEye,
  TbShieldLock,
} from 'react-icons/tb';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { API, postRequest } from '@/api/api';
import { getRefreshToken } from '@/api/tokens.helper';
import { type ApiException, getErrorStatus, isNotFound, isTooManyAttempts } from '@/common/error';
import { Header, Modal } from '@/components';
import { Button, ButtonOutlinedSmall } from '@/components/Button';
import { Input, PasswordInput, PhoneInput, Switch } from '@/components/Inputs';
import { isStrongPassword, WEAK_PASSWORD_MESSAGE } from '@/pages/Auth/passwordRules';
import PasswordStrength from '@/pages/Auth/PasswordStrength';
import CodeConfirm from '@/pages/Auth/TotpEnroll/CodeConfirm';
import CodeConfirmModal from '@/pages/Auth/TotpEnroll/CodeConfirmModal';
import RecoveryCodes from '@/pages/Auth/TotpEnroll/RecoveryCodes';
import notice from '@/pages/Auth/TotpEnroll/TotpEnroll.module.scss';
import TotpEnrollModal from '@/pages/Auth/TotpEnroll/TotpEnrollModal';
import {
  type AuthAccount,
  confirmLoginChange,
  getAuthAccount,
  requestLoginChange,
} from '@/store/auth/auth.api';
import { type TwoFactorEnabledResponse, useAuth } from '@/store/auth/auth.store';
import {
  type BiometryKind,
  canUseBiometric,
  disableBiometricLogin,
  enableBiometricLogin,
  getBiometryInfo,
  isBiometricEnabled,
} from '@/utils/biometric';
import { toast } from '@/utils/toast';

import { TWO_FACTOR_ROWS, type TwoFactorMethodKey } from './SecuritySettingsPage.data';
import s from './SecuritySettingsPage.module.scss';

const BIOMETRY_ICONS: Record<BiometryKind, IconType> = {
  face: TbFaceId,
  fingerprint: TbFingerprint,
  iris: TbScanEye,
  biometrics: TbShieldLock,
};

type LoginField = 'email' | 'phone';

const LOGIN_ROWS: { key: LoginField; icon: IconType; title: string }[] = [
  { key: 'email', icon: TbMail, title: 'Email' },
  { key: 'phone', icon: TbPhone, title: 'Mobile Number' },
];

const LOGIN_SUBJECT: Record<LoginField, string> = {
  email: 'email address',
  phone: 'mobile number',
};

const OAUTH_PROVIDER_NAMES: Record<string, string> = {
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  oidc: 'single sign-on',
};

/** "Google", "Google or Apple", "Google, Apple, or Facebook". */
const providerList = (providers: string[]): string => {
  const names = providers.map((p) => OAUTH_PROVIDER_NAMES[p] ?? p);
  if (names.length <= 2) return names.join(' or ');
  return `${names.slice(0, -1).join(', ')}, or ${names[names.length - 1]}`;
};

/**
 * A password reset is also how an account with social sign-in only gets its first
 * password: the code goes to the login given, and the account has a password after it.
 */
const useStartPasswordSetup = () => {
  const navigate = useNavigate();
  return (login?: string | null) =>
    navigate('/forgot-password', { state: login ? { email: login } : undefined });
};

/**
 * Why auth refused the current password a sensitive change asked for, or null when the
 * change failed for another reason.
 */
const passwordStepUpErrorText = (err: unknown): string | null => {
  if (isTooManyAttempts(err)) {
    return 'Too many wrong attempts. Please wait a few minutes and try again.';
  }
  if (getErrorStatus(err) === 401 && (err as Partial<ApiException>)?.type === 'INVALID_PASSWORD') {
    return 'That password doesn’t match. Please try again.';
  }
  return null;
};

type PasswordStepUp = {
  /** What the password unlocks, as one sentence under the field. */
  hint: string;
  actionLabel: string;
  /** Runs the change with the password; closes the prompt itself on success. */
  run: (password: string) => Promise<void>;
  /** What to say when the change fails for a reason other than the password. */
  failText: (err: unknown) => string;
};

/** Asks for the current password before a sensitive change, which auth checks again. */
const PasswordStepUpModal = ({
  hint,
  actionLabel,
  run,
  failText,
  onClose,
}: PasswordStepUp & {
  onClose: () => void;
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!password) {
      setError('Please enter your current password.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await run(password);
    } catch (err) {
      console.error('Failed to apply the change:', err);
      setError(passwordStepUpErrorText(err) ?? failText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Confirm It’s You">
      <form className={s.modalBody} onSubmit={submit} noValidate>
        <PasswordInput
          label="Current Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(undefined);
          }}
          error={error}
          autoFocus
        />
        <p className={s.modalHint}>{hint}</p>
        <Button type="submit" disabled={busy}>
          {busy ? 'Checking…' : actionLabel}
        </Button>
      </form>
    </Modal>
  );
};

/**
 * After a change that made auth sign the account out everywhere and this device couldn't
 * sign straight back in, closing can only mean signing in.
 */
const SignedOutModal = ({ title, message }: { title: string; message: string }) => {
  const signIn = () => void useAuth.getState().logout();

  return (
    <Modal open onClose={signIn} title={title}>
      <div className={s.modalBody}>
        <p className={s.modalHint}>{message}</p>
        <Button onClick={signIn}>Sign In</Button>
      </div>
    </Modal>
  );
};

/**
 * Turns a rejected login write — or the SMS enable that depends on it — into
 * something the user can act on. Auth wraps the Twilio or database reason, so it
 * only shows up in the text of the error body.
 */
const accountErrorText = (err: unknown, subject: string): string => {
  const e = err as { type?: string; dev_message?: string; error_message?: string } | null;
  const text = [e?.type, e?.dev_message, e?.error_message].filter(Boolean).join(' ');

  if (/DUPLICATE|already exists/i.test(text)) {
    return `Another account already uses this ${subject}. Please try a different one.`;
  }
  if (/matches the current/i.test(text)) {
    return `This is already your ${subject}. Please enter a different one to change it.`;
  }
  if (/not allowed for this account|required to change login/i.test(text)) {
    return `You need a password to change this ${subject}. Please set one under Password first.`;
  }
  if (/TWILIO|short code|not a valid phone/i.test(text)) {
    return 'Couldn’t text this number. Please check it or try a different one.';
  }
  if (/INPUTS|Invalid email or phone/i.test(text)) {
    return `Couldn’t use that ${subject}. Please check it and try again.`;
  }
  return `Couldn’t save this ${subject}. Please try again.`;
};

/**
 * Turns a rejected login-change code into what to do next. Auth reads the token once,
 * so a wrong code burns it as surely as an expired one — both need a fresh code.
 */
const loginCodeErrorText = (err: unknown, subject: string): string => {
  const e = err as { dev_message?: string; error_message?: string } | null;
  const text = [e?.dev_message, e?.error_message].filter(Boolean).join(' ');

  if (/invalid code/i.test(text)) {
    return 'That code didn’t match, and it only works once. Please tap Resend Code for a new one.';
  }
  if (/token not found/i.test(text)) {
    return 'This code has expired. Please tap Resend Code for a new one.';
  }
  return accountErrorText(err, subject);
};

/**
 * Adds or replaces one half of the account login. Auth only makes the change once the
 * new email or number proves it is reachable: saving sends a code to it, and the change
 * lands when that code comes back.
 */
const LoginChangeModal = ({
  userId,
  field,
  initial = '',
  title,
  hint,
  askPassword,
  resetLogin,
  oauthProviders = [],
  onClose,
  onChanged,
}: {
  userId: string;
  field: LoginField;
  initial?: string;
  title: string;
  hint?: string;
  /** Auth wants the current password before changing the login of an account that has one. */
  askPassword: boolean;
  /** The current login a password is set up through, for an account that has none. */
  resetLogin?: string | null;
  /** Social sign-ins linked to the account; they find it by its current email. */
  oauthProviders?: string[];
  onClose: () => void;
  /** Fired once the change has landed and this device is signed in again, with the updated account. */
  onChanged: (next: AuthAccount) => void | Promise<void>;
}) => {
  const [value, setValue] = useState(initial);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [sending, setSending] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const changed = useRef<AuthAccount | null>(null);
  const signedIn = useRef(false);
  const startPasswordSetup = useStartPasswordSetup();
  // Changing the email moves the account away from the one its social sign-ins match.
  const showProviderWarning = field === 'email' && !!initial && oauthProviders.length > 0;

  const login = value.trim();
  const valid =
    field === 'phone' ? isValidPhoneNumber(value) : z.string().email().safeParse(login).success;

  const send = async () => {
    if (!valid || sending) return;
    if (askPassword && !password) {
      setPasswordError('Please enter your current password.');
      return;
    }
    setSending(true);
    setError(undefined);
    setPasswordError(undefined);
    try {
      setSecret(await requestLoginChange(userId, login, password));
    } catch (err) {
      console.error('Failed to request the login change:', err);
      const passwordText = askPassword ? passwordStepUpErrorText(err) : null;
      if (passwordText) setPasswordError(passwordText);
      else setError(accountErrorText(err, LOGIN_SUBJECT[field]));
    } finally {
      setSending(false);
    }
  };

  if (signedOut) {
    return (
      <SignedOutModal
        title={field === 'phone' ? 'Mobile Number Changed' : 'Email Changed'}
        message={`Your ${LOGIN_SUBJECT[field]} has been changed and you’ve been signed out everywhere. Please sign in again.`}
      />
    );
  }

  // Auth only changes the login of an account with a password, so one comes first.
  if (!askPassword) {
    return (
      <Modal open onClose={onClose} title={title}>
        <div className={s.modalBody}>
          <p className={s.modalHint}>
            {`Please set a password before changing your ${LOGIN_SUBJECT[field]}. ${
              resetLogin
                ? `You’ll get a code at ${resetLogin} to set one.`
                : 'You’ll get a code to set one.'
            }`}
          </p>
          <Button onClick={() => startPasswordSetup(resetLogin)}>Set Password</Button>
        </div>
      </Modal>
    );
  }

  if (secret) {
    return (
      <Modal open onClose={onClose}>
        <CodeConfirm
          method={field === 'phone' ? 'sms' : 'email'}
          destination={login}
          verify={async (code) => {
            changed.current = await confirmLoginChange(userId, secret, code);
            // The change revoked every session, this one included.
            signedIn.current = await useAuth.getState().signInAgain(login, password);
          }}
          resend={async () => setSecret(await requestLoginChange(userId, login, password))}
          errorText={(err) => loginCodeErrorText(err, LOGIN_SUBJECT[field])}
          onDone={() => {
            if (!signedIn.current) setSignedOut(true);
            else if (changed.current) void onChanged(changed.current);
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={title}>
      <div className={s.modalBody}>
        {field === 'phone' ? (
          <PhoneInput
            label="Mobile Number"
            value={value}
            onChange={(v) => {
              setValue(v ?? '');
              setError(undefined);
            }}
            error={value && !valid ? 'Please enter a valid mobile number.' : error}
          />
        ) : (
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(undefined);
            }}
            error={value && !valid ? 'Please enter a valid email address.' : error}
          />
        )}
        {showProviderWarning && (
          <div className={notice.warningNotice}>
            <span className={notice.warningIcon}>⚠</span>
            <p>
              Signing in with {providerList(oauthProviders)} uses your current email, so it won’t
              open this account after the change. Please sign in with your new email and password
              instead.
            </p>
          </div>
        )}
        <PasswordInput
          label="Current Password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError(undefined);
          }}
          error={passwordError}
        />
        <p className={s.modalHint}>
          {hint ?? `You’ll get a code at this ${LOGIN_SUBJECT[field]} to confirm it’s yours.`}
        </p>
        <Button onClick={send} disabled={sending || !valid}>
          {sending ? 'Sending…' : 'Send Code'}
        </Button>
      </div>
    </Modal>
  );
};

/**
 * The email and mobile number the account signs in with. Both live on the auth user:
 * the profile's contact card edits people records, which are not logins, and each of
 * these is also where a code for its 2FA method is sent.
 */
const LoginGroup = ({
  account,
  onSaved,
}: {
  account: AuthAccount | null;
  onSaved: (next: AuthAccount) => void;
}) => {
  const [editing, setEditing] = useState<LoginField | null>(null);

  return (
    <>
      <p className={s.groupLabel}>Sign-In</p>
      <div className={s.group}>
        {LOGIN_ROWS.map(({ key, icon: Icon, title }) => (
          <div key={key} className={s.row}>
            <div className={s.rowIcon}>
              <Icon />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>{title}</div>
              <div className={s.rowSub}>{account?.[key] || 'Not set'}</div>
            </div>
            <div className={s.rowActions}>
              <ButtonOutlinedSmall onClick={() => setEditing(key)} disabled={!account}>
                <FiEdit2 />
                {account?.[key] ? 'Edit' : 'Add'}
              </ButtonOutlinedSmall>
            </div>
          </div>
        ))}
      </div>
      <p className={s.heroHint}>
        You can sign in with either of these. Changing one also changes where your two-factor codes
        go.
      </p>

      {editing && account && (
        <LoginChangeModal
          userId={account.id}
          field={editing}
          initial={account[editing] ?? ''}
          title={`${account[editing] ? 'Change' : 'Add'} ${
            editing === 'phone' ? 'mobile number' : 'email'
          }`}
          askPassword={account.has_password !== false}
          resetLogin={account.email ?? account.phone}
          oauthProviders={account.oauth_providers}
          onClose={() => setEditing(null)}
          onChanged={(next) => {
            onSaved(next);
            setEditing(null);
            toast.success(`Your ${LOGIN_SUBJECT[editing]} has been updated`);
          }}
        />
      )}
    </>
  );
};

const changePasswordSchema = z
  .object({
    current: z.string().min(1, 'Enter your current password'),
    next: z
      .string()
      .min(1, 'Enter a new password')
      .refine(isStrongPassword, { message: WEAK_PASSWORD_MESSAGE }),
    confirm: z.string().min(1, 'Enter the new password again'),
  })
  .refine((v) => !v.next || v.next !== v.current, {
    path: ['next'],
    message: 'Please choose a new password that’s different from your current one.',
  })
  .refine((v) => v.next === v.confirm, {
    path: ['confirm'],
    message: "The passwords don't match",
  });

type ChangePasswordValues = z.infer<typeof changePasswordSchema>;

/**
 * Says which field a rejected change belongs to and what to do about it. Auth checks the
 * current password itself (401, with a lockout) and refuses a new one that matches it.
 */
const changePasswordError = (
  err: unknown,
): { field: 'current' | 'next' | 'root'; message: string } => {
  const e = err as Partial<ApiException> | null;

  if (isTooManyAttempts(err)) {
    return {
      field: 'current',
      message: 'Too many wrong attempts. Wait a few minutes and try again.',
    };
  }
  if (isNotFound(err) || /AUTHENTICATION_METHOD/.test(e?.type ?? '')) {
    return {
      field: 'root',
      message: "Your account signs in without a password, so there isn't one to change.",
    };
  }
  if (e?.type === 'INVALID_PASSWORD' && e.status === 401) {
    return { field: 'current', message: 'Your current password is incorrect' };
  }
  if ((e?.type === 'INVALID_PASSWORD' && e.status === 400) || e?.status === 409) {
    return {
      field: 'next',
      message: 'Please choose a new password that’s different from your current one.',
    };
  }
  if (e?.type === 'INPUTS') {
    return { field: 'next', message: WEAK_PASSWORD_MESSAGE };
  }
  if (!e?.domain) {
    return {
      field: 'root',
      message: "Couldn't reach the server. Check your connection and try again.",
    };
  }
  return { field: 'root', message: "Couldn't change your password. Try again." };
};

/**
 * Changes the password the account signs in with. Auth signs every session out when it
 * does — other devices stay signed out, and this one is signed straight back in unless
 * the account has two-factor on, in which case the user signs in again here too.
 */
const ChangePasswordModal = ({ login, onClose }: { login: string; onClose: () => void }) => {
  const [signedOut, setSignedOut] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { current: '', next: '', confirm: '' },
  });
  const next = useWatch({ control, name: 'next' });

  const onSubmit: SubmitHandler<ChangePasswordValues> = async ({ current, next }) => {
    try {
      const stillSignedIn = await useAuth.getState().changePassword(login, current, next);
      if (!stillSignedIn) {
        setSignedOut(true);
        return;
      }
      toast.success('Your password has been changed');
      onClose();
    } catch (err) {
      console.error('Failed to change the password:', err);
      const { field, message } = changePasswordError(err);
      setError(field, { type: 'server', message });
    }
  };

  if (signedOut) {
    return (
      <SignedOutModal
        title="Password Changed"
        message="Your password has been changed and you’ve been signed out everywhere. Please sign in again with your new password."
      />
    );
  }

  return (
    <Modal open onClose={onClose} title="Change password">
      <form className={s.modalBody} onSubmit={handleSubmit(onSubmit)} noValidate>
        <PasswordInput
          label="Current password"
          autoComplete="current-password"
          {...register('current')}
          error={errors.current?.message}
        />
        <PasswordInput
          label="New password"
          autoComplete="new-password"
          {...register('next')}
          error={errors.next?.message}
        />
        <PasswordStrength password={next} />
        <PasswordInput
          label="Confirm new password"
          autoComplete="new-password"
          {...register('confirm')}
          error={errors.confirm?.message}
        />
        {errors.root && (
          <p role="alert" className={s.formError}>
            {errors.root.message}
          </p>
        )}
        <p className={s.modalHint}>You&apos;ll be signed out on your other devices.</p>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Changing…' : 'Change password'}
        </Button>
      </form>
    </Modal>
  );
};

const PasswordGroup = ({ account }: { account: AuthAccount | null }) => {
  const [open, setOpen] = useState(false);
  const login = account?.email ?? account?.phone;
  const hasPassword = account?.has_password !== false;
  const startPasswordSetup = useStartPasswordSetup();

  return (
    <>
      <p className={s.groupLabel}>Password</p>
      <div className={s.group}>
        <div className={s.row}>
          <div className={s.rowIcon}>
            <TbKey />
          </div>
          <div className={s.rowText}>
            <div className={s.rowTitle}>Password</div>
            <div className={s.rowSub}>
              {hasPassword
                ? 'Change the password you sign in with.'
                : 'Set one to sign in with your email or mobile number too.'}
            </div>
          </div>
          <div className={s.rowActions}>
            {hasPassword ? (
              <ButtonOutlinedSmall onClick={() => setOpen(true)} disabled={!login}>
                <FiEdit2 />
                Change
              </ButtonOutlinedSmall>
            ) : (
              <ButtonOutlinedSmall onClick={() => startPasswordSetup(login)}>
                <FiEdit2 />
                Set Password
              </ButtonOutlinedSmall>
            )}
          </div>
        </div>
      </div>

      {open && login && <ChangePasswordModal login={login} onClose={() => setOpen(false)} />}
    </>
  );
};

const TwoFactorGroup = ({
  account,
  onAccount,
}: {
  account: AuthAccount | null;
  onAccount: (next: AuthAccount) => void;
}) => {
  const session = useAuth((st) => st.session);
  const profilePhone = session?.contacts?.find((c) => c.contact_type === 'phone')?.contact;
  const smsUserId = account?.id ?? session?.id;

  const [status, setStatus] = useState<TwoFactorEnabledResponse | null>(null);
  const [busy, setBusy] = useState<TwoFactorMethodKey | null>(null);
  const [qrCode, setQrCode] = useState('');
  const [totpUrl, setTotpUrl] = useState('');
  const [codeMethod, setCodeMethod] = useState<'email' | 'sms' | null>(null);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [stepUp, setStepUp] = useState<PasswordStepUp | null>(null);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);
  // Unknown until the account loads; asking then is the safe guess.
  const hasPassword = account?.has_password !== false;

  /** Runs a change auth guards with the current password, asking for it when there is one. */
  const withPassword = async (step: PasswordStepUp) => {
    if (hasPassword) {
      setStepUp(step);
      return;
    }
    try {
      await step.run('');
    } catch (err) {
      console.error('Failed to apply the change:', err);
      toast.error(step.failText(err));
    }
  };

  // Load the current status on mount. Store actions and the setter are stable, so the
  // effect needs no extra dependencies.
  useEffect(() => {
    useAuth
      .getState()
      .getTwoFactorEnabled()
      .then(setStatus)
      .catch((err) => console.error('Failed to load 2FA status:', err));
  }, []);

  const refresh = async () => {
    try {
      setStatus(await useAuth.getState().getTwoFactorEnabled());
    } catch (err) {
      console.error('Failed to refresh 2FA status:', err);
    }
  };

  const enabledCount = status
    ? (['email', 'totp', 'sms'] as TwoFactorMethodKey[]).filter((m) => status[m]).length
    : 0;
  // The default only matters once the user has a choice to make.
  const showDefaultPicker = enabledCount >= 2;

  const toggle = async (method: TwoFactorMethodKey) => {
    if (busy) return;
    const auth = useAuth.getState();

    if (status?.[method]) {
      await withPassword({
        hint: 'Please enter your current password to turn off this method.',
        actionLabel: 'Turn Off',
        run: async (password) => {
          await auth.disableTwoFactorMethod(method, password);
          setStepUp(null);
          await refresh();
          toast.success('Your two-factor method has been turned off.');
        },
        failText: () => 'Couldn’t update your two-factor settings. Please try again.',
      });
      return;
    }

    if (method === 'totp') {
      await withPassword({
        hint: 'Please enter your current password to set up an authenticator app.',
        actionLabel: 'Continue',
        run: async (password) => {
          const { qr_code, url } = await auth.enableTotp(password);
          setStepUp(null);
          setQrCode(qr_code);
          setTotpUrl(url);
          await refresh();
        },
        failText: () => 'Couldn’t update your two-factor settings. Please try again.',
      });
      return;
    }

    setBusy(method);
    try {
      /* Both code methods send to the login on the auth account, so neither can be
         armed without that half of it. Re-read the account when it hasn't loaded:
         treating a failed load as "no number" would overwrite a login that is there. */
      const current = account ?? (await getAuthAccount());
      onAccount(current);

      if (method === 'sms') {
        // With a phone login already on the account — the number the Login block
        // shows — arm SMS straight away; only ask when the account has none.
        if (!current.phone) {
          setPhoneModalOpen(true);
          return;
        }

        await auth.enableTwoFactorMethod('sms');
        setCodeMethod('sms');
        return;
      }

      if (!current.email) {
        toast.error('Please add an email address under Sign-in first.');
        return;
      }

      // Same for email: arm + send code, then confirm in the modal.
      await auth.enableTwoFactorMethod('email');
      setCodeMethod('email');
    } catch (err) {
      console.error('Failed to update 2FA method:', err);
      toast.error('Couldn’t update your two-factor settings. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  /* The code is texted to the phone on the *auth* account, and auth fills that column
     from the account login alone — a phone contact written to the people service never
     reaches it, so enabling SMS failed with "No phone number found". The prompt only
     opens for an account with no phone login, so it adds one — confirmed by a code of
     its own first; auth leaves the email half of the login untouched. */
  const enableSmsWithPhone = async (next: AuthAccount) => {
    setPhoneModalOpen(false);
    onAccount(next);
    try {
      // Keep the profile in step when it has no number of its own yet; an existing one
      // is the user's and isn't ours to overwrite from the security screen.
      if (!profilePhone && next.phone) {
        await postRequest(API.PEOPLE, `/${next.id}/contacts`, {
          contacts: [{ contact_type: 'phone', contact: next.phone }],
        });
        await useAuth.getState().me();
      }

      await useAuth.getState().enableTwoFactorMethod('sms'); // arms + sends the code
      setCodeMethod('sms'); // confirm the code before SMS counts as enabled
    } catch (err) {
      console.error('Failed to enable SMS 2FA:', err);
      toast.error('Couldn’t turn on SMS codes. Please try again.');
    }
  };

  const makeDefault = (method: TwoFactorMethodKey) =>
    withPassword({
      hint: 'Please enter your current password to change your default method.',
      actionLabel: 'Set as Default',
      run: async (password) => {
        await useAuth.getState().setDefaultTwoFactor(method, password);
        setStepUp(null);
        await refresh();
      },
      failText: () => 'Couldn’t set the default method. Please try again.',
    });

  // The old codes stop working once auth hands out the new set, shown here once.
  const replaceRecoveryCodes = () =>
    withPassword({
      hint: 'Please enter your current password. Your old recovery codes will stop working.',
      actionLabel: 'Replace Codes',
      run: async (password) => {
        const codes = await useAuth.getState().regenerateRecoveryCodes(password);
        setStepUp(null);
        setNewRecoveryCodes(codes);
      },
      failText: (err) =>
        getErrorStatus(err) === 409
          ? 'Please turn on a two-factor method first.'
          : 'Couldn’t replace your recovery codes. Please try again.',
    });

  const anyMethodOn =
    !!status && (['email', 'totp', 'sms', 'call'] as const).some((m) => status[m]);

  return (
    <>
      <p className={s.groupLabel}>Two-Factor Authentication</p>
      <div className={s.group}>
        {TWO_FACTOR_ROWS.map(({ key, icon: Icon, title, sub }) => {
          const on = !!status?.[key];
          const isDefault = status?.default === key;
          return (
            <div key={key} className={clsx(s.row, busy === key && s.rowBusy)}>
              <div className={s.rowIcon}>
                <Icon />
              </div>
              <div className={s.rowText}>
                <div className={s.rowTitle}>{title}</div>
                <div className={s.rowSub}>
                  {on && showDefaultPicker ? (
                    isDefault ? (
                      'Default method'
                    ) : (
                      <button type="button" className={s.linkBtn} onClick={() => makeDefault(key)}>
                        Set as Default
                      </button>
                    )
                  ) : (
                    sub
                  )}
                </div>
              </div>
              <Switch checked={on} onChange={() => toggle(key)} disabled={busy !== null} />
            </div>
          );
        })}
        {anyMethodOn && (
          <div className={s.row}>
            <div className={s.rowIcon}>
              <TbLifebuoy />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>Recovery Codes</div>
              <div className={s.rowSub}>Replace them if they’re lost or mostly used.</div>
            </div>
            <div className={s.rowActions}>
              <ButtonOutlinedSmall onClick={replaceRecoveryCodes}>
                Replace Codes
              </ButtonOutlinedSmall>
            </div>
          </div>
        )}
      </div>
      <p className={s.heroHint}>
        Require a second step when signing in. We&apos;ll prompt for your default method first.
      </p>

      {qrCode && (
        <TotpEnrollModal
          qrCode={qrCode}
          url={totpUrl}
          onClose={() => {
            setQrCode('');
            setTotpUrl('');
          }}
          onConfirmed={async () => {
            await refresh();
            toast.success('Authenticator app enabled');
          }}
        />
      )}

      {codeMethod && (
        <CodeConfirmModal
          method={codeMethod}
          onClose={() => setCodeMethod(null)}
          onConfirmed={async () => {
            await refresh();
            toast.success(
              codeMethod === 'sms' ? 'SMS two-factor enabled' : 'Email two-factor enabled',
            );
          }}
        />
      )}

      {stepUp && <PasswordStepUpModal {...stepUp} onClose={() => setStepUp(null)} />}

      {newRecoveryCodes && (
        <Modal open onClose={() => setNewRecoveryCodes(null)}>
          <RecoveryCodes codes={newRecoveryCodes} onDone={() => setNewRecoveryCodes(null)} />
        </Modal>
      )}

      {phoneModalOpen && smsUserId && (
        <LoginChangeModal
          userId={smsUserId}
          field="phone"
          askPassword={hasPassword}
          resetLogin={account?.email ?? account?.phone}
          initial={profilePhone ?? ''}
          title="Mobile Number for SMS"
          hint="Sign-in codes go to this number, and you can sign in with it. A code comes first to confirm it’s yours."
          onClose={() => setPhoneModalOpen(false)}
          onChanged={enableSmsWithPhone}
        />
      )}
    </>
  );
};

const SecurityContent = () => {
  // Capability is probed once on mount and held in state, so the toggle's click
  // handler can call enableBiometricLogin() with minimal async before WebAuthn —
  // Safari requires the prompt to fire within the click's transient activation.
  const [ready, setReady] = useState(false);
  const [available, setAvailable] = useState(false);
  const [enabled, setEnabled] = useState(isBiometricEnabled());
  const [label, setLabel] = useState('Biometrics');
  const [kind, setKind] = useState<BiometryKind>('biometrics');
  const [busy, setBusy] = useState(false);
  // The auth account is the login side of the user — its email and phone are both
  // what signs in and where code-based 2FA is sent, so both blocks below read it.
  const [account, setAccount] = useState<AuthAccount | null>(null);

  const email = useAuth(
    (st) => st.session?.contacts?.find((c) => c.contact_type === 'email')?.contact,
  );

  useEffect(() => {
    let active = true;
    (async () => {
      const [can, info] = await Promise.all([canUseBiometric(), getBiometryInfo()]);
      if (!active) return;
      setAvailable(can);
      setLabel(info.label);
      setKind(info.kind);
      setReady(true);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    getAuthAccount()
      .then(setAccount)
      .catch((err) => console.error('Failed to load the auth account:', err));
  }, []);

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (enabled) {
        await disableBiometricLogin();
        setEnabled(false);
        toast.success(`Your ${label} sign-in has been turned off.`);
        return;
      }

      const token = getRefreshToken();
      if (!token) {
        toast.error(`Please sign in again to turn on ${label} sign-in.`);
        return;
      }
      // Web: this triggers the WebAuthn credential creation + biometric prompt.
      await enableBiometricLogin(token, email);
      setEnabled(true);
      toast.success(`Your ${label} sign-in has been turned on.`);
    } catch (err) {
      // Common web failure: the browser/authenticator lacks the PRF extension, or
      // the user dismissed the system prompt. Keep the toggle off and be honest.
      console.error('Failed to update biometric login:', err);
      toast.error(
        enabled
          ? 'Couldn’t turn off biometric sign-in. Please try again.'
          : `Couldn’t turn on ${label} sign-in. Please try again.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const Icon = BIOMETRY_ICONS[kind];

  const subtitle = !ready
    ? 'Checking availability…'
    : !available
      ? 'Not available on this device or browser'
      : enabled
        ? 'Skip your password and unlock with your face or fingerprint.'
        : 'Use biometrics to sign in faster next time.';

  return (
    <div className={s.page}>
      <header className={s.detailHeader}>
        <div className={s.detailIcon}>
          <TbShieldLock />
        </div>
        <div>
          <h2 className={s.detailTitle}>Security</h2>
          <p className={s.detailSub}>Manage how you sign in to VesselHaven.</p>
        </div>
      </header>

      <p className={s.groupLabel}>Sign-in</p>
      <div className={s.group}>
        <div className={clsx(s.row, busy && s.rowBusy)}>
          <div className={s.rowIcon}>
            <Icon />
          </div>
          <div className={s.rowText}>
            <div className={s.rowTitle}>{label} login</div>
            <div className={s.rowSub}>{subtitle}</div>
          </div>
          {ready && available && <Switch checked={enabled} onChange={handleToggle} />}
        </div>
      </div>
      <p className={s.heroHint}>
        Your biometrics never leave this device. We only keep an encrypted sign-in token that your
        face or fingerprint unlocks.
      </p>

      <PasswordGroup account={account} />

      <LoginGroup account={account} onSaved={setAccount} />

      <TwoFactorGroup account={account} onAccount={setAccount} />
    </div>
  );
};

export default function SecuritySettingsPage({ embedded = false }: { embedded?: boolean } = {}) {
  return (
    <>
      {!embedded && <Header title="Security" backButton />}
      <SecurityContent />
    </>
  );
}
